import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { Not, RuleInterface, StringEquals } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyInputModule, SelectInputOptionInterface } from '@vendasta/galaxy/input';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { map } from 'rxjs/operators';
import { CommonTriggerPanelComponent } from '../../../common/common-trigger-panel.component';
import { TriggerBaseRules } from '../../../common/decorators/trigger-rules.decorator';
import { NoRules } from '../../common';

import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { Observable } from 'rxjs';

const BOTH_WAYS = 'inbox message is sent or received';
const OUTBOUND_ONLY = 'inbox message is sent';
const INBOUND_ONLY = 'inbox message is received';

@Component({
  selector: 'automata-inbox-message-sent-to-vmf-panel',
  styleUrls: ['./inbox-message-sent-to-vmf-panel.component.scss'],
  templateUrl: './inbox-message-sent-to-vmf-panel.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
    GalaxyInputModule,
  ],
})
export class InboxMessageSentToVmfPanelComponent extends CommonTriggerPanelComponent implements OnInit {
  triggerStartsControl: UntypedFormControl = new UntypedFormControl('');
  triggerStartsOptions: SelectInputOptionInterface[] = [
    { label: 'AUTOMATIONS.EDITOR.TRIGGERS.INBOX_MESSAGE_SENT_TO_VMF.TRIGGER_OPTION.BOTH_WAYS', value: BOTH_WAYS },
    {
      label: 'AUTOMATIONS.EDITOR.TRIGGERS.INBOX_MESSAGE_SENT_TO_VMF.TRIGGER_OPTION.OUTBOUND_ONLY',
      value: OUTBOUND_ONLY,
    },
    { label: 'AUTOMATIONS.EDITOR.TRIGGERS.INBOX_MESSAGE_SENT_TO_VMF.TRIGGER_OPTION.INBOUND_ONLY', value: INBOUND_ONLY },
  ];
  partnerId = '';

  constructor(
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) protected readonly partnerId$: Observable<string>,
    private readonly destroyRef: DestroyRef,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    this.triggerStartsControl.setValue(jsonData.trigger_starts_option);
    this.partnerId$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((partnerId) => (this.partnerId = partnerId)),
      )
      .subscribe();

    this.savingEnabled$.next(true);
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.triggerStartsControl];
  }

  isFormValid(): boolean {
    return !this.triggerStartsControl.invalid;
  }

  @TriggerBaseRules
  getBaseRules(): RuleInterface[] {
    switch (this.triggerStartsControl.value) {
      case INBOUND_ONLY:
        return [new Not(new StringEquals('{.sender_org_internal_id}', this.partnerId, '')).toRuleInterface()];
      case OUTBOUND_ONLY:
        return [new StringEquals('{.sender_org_internal_id}', this.partnerId, '').toRuleInterface()];
      case BOTH_WAYS:
        return NoRules();
    }
  }

  fillRuleValues(_rules: RuleInterface[]): RuleInterface[] {
    return this.getBaseRules();
  }

  getData(): any {
    return {
      trigger_starts_option: this.triggerStartsControl.value,
    };
  }
}
